import { useEffect, useState } from "react";
import { doc, onSnapshot, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import type { Mood as MoodDoc } from "../types";

const OPTIONS: { emoji: string; label: string }[] = [
  { emoji: "😊", label: "Gut" },
  { emoji: "😐", label: "Okay" },
  { emoji: "😔", label: "Nicht so gut" },
  { emoji: "😍", label: "Verliebt" },
  { emoji: "😴", label: "Müde" },
  { emoji: "😤", label: "Gestresst" },
];

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function Mood() {
  const { user } = useAuth();
  const { couple, partner } = useCouple();
  const [mine, setMine] = useState<MoodDoc | null>(null);
  const [partnerMood, setPartnerMood] = useState<MoodDoc | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!couple || !user) return;
    const unsub = onSnapshot(doc(db, "couples", couple.id, "moods", user.uid), (snap) => {
      setMine(snap.exists() ? (snap.data() as MoodDoc) : null);
    });
    return unsub;
  }, [couple, user]);

  // Pulls the saved note into the input once today's doc first arrives, so
  // reopening the page shows what you wrote instead of a blank field.
  useEffect(() => {
    if (mine && mine.date === todayISO()) setNote(mine.note ?? "");
  }, [mine]);

  useEffect(() => {
    if (!couple || !partner) {
      setPartnerMood(null);
      return;
    }
    const unsub = onSnapshot(doc(db, "couples", couple.id, "moods", partner.uid), (snap) => {
      setPartnerMood(snap.exists() ? (snap.data() as MoodDoc) : null);
    });
    return unsub;
  }, [couple, partner]);

  async function setMood(option: { emoji: string; label: string }) {
    if (!couple || !user) return;
    setSaving(true);
    try {
      await setDoc(doc(db, "couples", couple.id, "moods", user.uid), {
        uid: user.uid,
        emoji: option.emoji,
        label: option.label,
        note: note.trim(),
        date: todayISO(),
        updatedAt: serverTimestamp(),
      });
    } finally {
      setSaving(false);
    }
  }

  const isToday = (m: MoodDoc | null) => !!m && m.date === todayISO();

  // A mood must exist for today before a note can attach to it — if you
  // haven't picked one yet, saving the note happens as part of setMood()
  // instead once you do.
  async function saveNoteIfPossible() {
    if (!couple || !user || !isToday(mine)) return;
    await updateDoc(doc(db, "couples", couple.id, "moods", user.uid), { note: note.trim() });
  }

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-24 pt-6">
      <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">Stimmung</h1>
      <p className="mt-1 text-sm text-ink-400 dark:text-night-faint">Ein kurzer Check-in, den ihr beide seht.</p>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <MoodCard title="Du" mood={isToday(mine) ? mine : null} />
        <MoodCard title={partner?.displayName ?? "Partner"} mood={isToday(partnerMood) ? partnerMood : null} />
      </div>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Wie geht's dir heute?</h2>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {OPTIONS.map((o) => (
            <button
              key={o.label}
              disabled={saving}
              onClick={() => setMood(o)}
              className={`card card-interactive flex flex-col items-center gap-1 rounded-2xl py-4 ${
                mine?.label === o.label && isToday(mine) ? "ring-2 ring-plum-600" : ""
              }`}
            >
              <span className="text-2xl">{o.emoji}</span>
              <span className="text-xs text-ink-600 dark:text-night-muted">{o.label}</span>
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Kurze Notiz (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={saveNoteIfPossible}
          className="mt-3 h-11 w-full rounded-lg border border-cream-200 bg-white px-3 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
        />
        {!isToday(mine) && note.trim() && (
          <p className="mt-1.5 text-xs text-ink-400 dark:text-night-faint">Wird gespeichert, sobald du oben eine Stimmung auswählst.</p>
        )}
      </section>
    </main>
  );
}

function MoodCard({ title, mood }: { title: string; mood: MoodDoc | null }) {
  return (
    <div className="card rounded-2xl p-4 text-center">
      <p className="text-xs font-medium uppercase tracking-wider text-ink-400 dark:text-night-faint">{title}</p>
      {mood ? (
        <>
          <p className="mt-2 text-3xl">{mood.emoji}</p>
          <p className="mt-1 text-sm font-medium text-ink-900 dark:text-night-text">{mood.label}</p>
          {mood.note && <p className="mt-1 text-xs text-ink-400 dark:text-night-faint">{mood.note}</p>}
        </>
      ) : (
        <p className="mt-4 text-sm text-ink-400 dark:text-night-faint">Noch kein Status heute</p>
      )}
    </div>
  );
}
